// =============================================================================
// FILE: packages/renderer/src/hooks/useProjects.ts
// =============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { call } from '../lib/api.js';
import { IPC } from '@bim/shared/constants/ipc.constants.js';
import { getPersistentProjects, savePersistentProject } from '../lib/localProjectPersistence.js';

export interface ProjectRow {
  id: string; projectNumber: string; name: string; client: string;
  phase: string; status: string;
  color?: string;
  clientAddress?: string;
  clientTimezone?: string;                // IANA tz — single source of truth for Calendar / Dashboard clocks / future Email & Reminders
  startDate?: string;
  endDate?: string;
  description?: string;
}
interface ProjectListPage { items: ProjectRow[]; total: number; page: number; pageSize: number; totalPages: number; }

const KEYS = { list: (p=1, ps=50) => ['projects','list',p,ps] as const, get: (id: string) => ['projects','get',id] as const };

export function useProjects(page = 1, pageSize = 50) {
  return useQuery({
    queryKey: KEYS.list(page, pageSize),
    queryFn: async () => {
      const remote = await call<{ params: { page: number; pageSize: number } }, ProjectListPage>(IPC.PROJECT_LIST, { params: { page, pageSize } });
      const local = await getPersistentProjects();
      const byId = new Map<string, ProjectRow>();
      [...local, ...(remote.items ?? [])].forEach((row) => byId.set(row.id, row));
      const items = Array.from(byId.values()).slice(0, pageSize);
      return { ...remote, items, total: items.length, page, pageSize, totalPages: Math.max(1, Math.ceil(items.length / pageSize)) };
    },
  });
}

export function useProject(id: string) {
  return useQuery({ queryKey: KEYS.get(id), queryFn: () => call<{ id: string }, ProjectRow>(IPC.PROJECT_GET, { id }), enabled: !!id });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: Partial<ProjectRow>) => {
      const row = await call<{ dto: Partial<ProjectRow> }, ProjectRow>(IPC.PROJECT_CREATE, { dto });
      await savePersistentProject(row, { source: 'ipc' });
      return row;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projects'] }),
  });
}

export function useUpdateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: Partial<ProjectRow> }) =>
      call<{ id: string; dto: Partial<ProjectRow> }, ProjectRow>(IPC.PROJECT_UPDATE, { id, dto }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['projects'] });
      qc.invalidateQueries({ queryKey: KEYS.get(vars.id) });
    },
  });
}

export function useArchiveProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => call<{ id: string }, { ok: boolean }>(IPC.PROJECT_ARCHIVE, { id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projects'] }),
  });
}
